/**
 * Graph construction algorithm for the IVR Journey Visualisation.
 * Traverses configuration relationships from a DNIS entry point outward,
 * building React Flow nodes and edges, then applies Dagre layout.
 *
 * BR-14: REQ-3 — Automatic Graph Construction
 */

import dagre from '@dagrejs/dagre';
import { Node, Edge, Position } from 'reactflow';
import { ConfigurationObject } from '@features/configs/services/configs-api';
import { CONFIG_REFERENCE_FIELDS, NODE_ROLE_MAP, ROLE_COLOURS, getEdgeLabel } from '../components/nodes/nodeConfig';

const MAX_DEPTH = 10;

export interface GraphData {
  nodes: Node[];
  edges: Edge[];
}

export interface JourneyNodeData {
  configId: string;
  configName: string;
  configType: string;
  role: string;
  roleColour: string;
  config: ConfigurationObject;
  childCount?: number;
  isCollapsed?: boolean;
  hasPendingDeletionWarning?: boolean;
}

/**
 * Builds the journey graph by traversing config relationships from a start DNIS.
 *
 * @param startConfigId - The DNIS configId to start traversal from
 * @param allConfigs - All configurations from the portal
 * @returns GraphData with positioned nodes and edges for React Flow
 */
export function buildGraph(startConfigId: string, allConfigs: ConfigurationObject[]): GraphData {
  const configMap = new Map(allConfigs.map((c) => [c.configId, c]));
  // Build name→configs lookup (multiple configs can share a name)
  const nameToConfigs = new Map<string, ConfigurationObject[]>();
  for (const c of allConfigs) {
    const key = c.configName.toLowerCase();
    const existing = nameToConfigs.get(key) ?? [];
    existing.push(c);
    nameToConfigs.set(key, existing);
  }

  const visited = new Set<string>();
  const nodes: Node[] = [];
  const edges: Edge[] = [];

  /**
   * Resolves a reference value to a config, with type preference.
   * Returns the config object (not just ID) so we can use type-qualified node IDs.
   */
  function resolveConfig(value: string, preferredType?: string): ConfigurationObject | null {
    // Find all configs matching this value by configId or configName
    const matches: ConfigurationObject[] = [];

    // Check by configId — may find multiple with same ID but different types
    for (const c of allConfigs) {
      if (c.configId === value || c.configId.toLowerCase() === value.toLowerCase()) {
        matches.push(c);
      }
    }

    // If no match by configId, try by configName
    if (matches.length === 0) {
      const byName = nameToConfigs.get(value.toLowerCase());
      if (byName) matches.push(...byName);
    }

    if (matches.length === 0) return null;
    if (matches.length === 1) return matches[0];

    // Multiple matches — prefer by type
    if (preferredType) {
      const preferred = matches.find((c) => c.configType === preferredType);
      if (preferred) return preferred;
    }

    // Default: return first match
    return matches[0];
  }

  /** Creates a unique node ID for the graph (type-qualified to handle name collisions) */
  function nodeId(config: ConfigurationObject): string {
    return `${config.configType}/${config.configId}`;
  }

  function traverse(config: ConfigurationObject, depth: number): void {
    const nid = nodeId(config);
    if (visited.has(nid) || depth > MAX_DEPTH) return;
    visited.add(nid);

    const role = NODE_ROLE_MAP[config.configType] ?? 'routing';
    const roleColour = ROLE_COLOURS[role] ?? ROLE_COLOURS.routing;

    const hasPendingDeletionWarning = checkPendingDeletionRefs(config, configMap);

    nodes.push({
      id: nid,
      type: 'ivrNode',
      position: { x: 0, y: 0 },
      data: {
        configId: config.configId,
        configName: config.configName,
        configType: config.configType,
        role,
        roleColour,
        config,
        hasPendingDeletionWarning,
      } as JourneyNodeData,
    });

    // Get reference fields for this config type
    const refFields = CONFIG_REFERENCE_FIELDS[config.configType] ?? [];

    for (const field of refFields) {
      const rawValue = config.payload?.[field] as string | undefined;
      if (!rawValue || typeof rawValue !== 'string') continue;

      // Determine preferred target type based on field context
      let preferredType: string | undefined;
      if (field.includes('Intent')) preferredType = 'intents';
      else if (field === 'businessHoursSchedule') preferredType = 'business-hours';
      else if (field === 'holidaySchedule') preferredType = 'holidays';

      const targetConfig = resolveConfig(rawValue, preferredType);
      if (!targetConfig) continue;

      const targetNid = nodeId(targetConfig);
      const label = getEdgeLabel(field);

      edges.push({
        id: `${nid}-${field}-${targetNid}`,
        source: nid,
        target: targetNid,
        label: label || undefined,
        type: 'smoothstep',
      });

      traverse(targetConfig, depth + 1);
    }

    // For intents with destinationType=menu: traverse into the menu
    if (config.configType === 'intents') {
      const destValue = config.payload?.destinationValue as string | undefined;
      const destType = config.payload?.destinationType as string | undefined;
      if (destValue && (destType === 'menu' || destType === 'queue')) {
        const targetType = destType === 'menu' ? 'menus' : 'queues';
        const menuConfig = resolveConfig(destValue, targetType);
        if (menuConfig) {
          const menuNid = nodeId(menuConfig);
          if (!visited.has(menuNid)) {
            edges.push({
              id: `${nid}-dest-${menuNid}`,
              source: nid,
              target: menuNid,
              label: '',
              type: 'smoothstep',
            });
            traverse(menuConfig, depth + 1);
          }
        }
      }
    }
  }

  // Start traversal from the DNIS entry point
  const startConfig = allConfigs.find((c) => c.configId === startConfigId && c.configType === 'dnis')
    ?? configMap.get(startConfigId);
  if (startConfig) {
    traverse(startConfig, 0);
  }

  // Count children for collapse toggle
  for (const node of nodes) {
    const childEdges = edges.filter((e) => e.source === node.id);
    (node.data as JourneyNodeData).childCount = childEdges.length;
  }

  // Apply Dagre layout
  return applyDagreLayout(nodes, edges);
}

/**
 * Checks if any reference field in a config points to a pending-deletion config.
 */
function checkPendingDeletionRefs(
  config: ConfigurationObject,
  configMap: Map<string, ConfigurationObject>
): boolean {
  const refFields = CONFIG_REFERENCE_FIELDS[config.configType] ?? [];
  for (const field of refFields) {
    const rawValue = config.payload?.[field] as string | undefined;
    if (!rawValue) continue;
    // Check by configId first
    let target = configMap.get(rawValue);
    // Fallback: check by configName
    if (!target) {
      for (const c of configMap.values()) {
        if (c.configName.toLowerCase() === rawValue.toLowerCase()) {
          target = c;
          break;
        }
      }
    }
    if (target?.deletionScheduledAt) return true;
  }
  return false;
}

/**
 * Applies Dagre left-to-right layout to position nodes.
 */
export function applyDagreLayout(nodes: Node[], edges: Edge[]): GraphData {
  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));
  dagreGraph.setGraph({
    rankdir: 'LR',
    ranksep: 80,
    nodesep: 24,
    marginx: 40,
    marginy: 40,
  });

  const nodeWidth = 168;
  const nodeHeight = 56;

  for (const node of nodes) {
    dagreGraph.setNode(node.id, { width: nodeWidth, height: nodeHeight });
  }

  for (const edge of edges) {
    dagreGraph.setEdge(edge.source, edge.target);
  }

  dagre.layout(dagreGraph);

  const positionedNodes = nodes.map((node) => {
    const nodeWithPosition = dagreGraph.node(node.id);
    return {
      ...node,
      position: {
        x: nodeWithPosition.x - nodeWidth / 2,
        y: nodeWithPosition.y - nodeHeight / 2,
      },
      sourcePosition: Position.Right,
      targetPosition: Position.Left,
    };
  });

  return { nodes: positionedNodes, edges };
}
